import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "addressInput",
    "latitudeInput",
    "longitudeInput",
    "mapsUrlInput",
    "nameInput",
    "placeIdInput",
    "status",
    "suggestions"
  ]

  static values = {
    detailsPath: String,
    enabled: Boolean,
    errorText: String,
    idleText: String,
    loadingText: String,
    minLength: Number,
    missingQueryText: String,
    noResultsText: String,
    refreshAppliedText: String,
    selectionAppliedText: String,
    suggestionsPath: String
  }

  connect() {
    this.abortController = null
    this.debounceTimer = null
    this.updateStatus()
    this.hideSuggestions()
  }

  disconnect() {
    this.abortPendingRequest()
    this.clearDebounce()
  }

  search(event) {
    if (!this.enabledValue) return

    this.placeIdInputTarget.value = ""
    this.updateStatus()

    const query = this.lookupQuery(event?.currentTarget)
    if (query.length < this.minLengthValue) {
      this.clearDebounce()
      this.hideSuggestions()
      this.updateStatus()
      return
    }

    this.clearDebounce()
    this.debounceTimer = window.setTimeout(() => this.fetchSuggestions(query), 250)
  }

  async refresh(event) {
    event.preventDefault()
    if (!this.enabledValue) return

    const placeId = this.placeIdInputTarget.value.trim()
    const query = this.lookupQuery()

    if (!placeId && query.length < this.minLengthValue) {
      this.updateStatus(this.missingQueryTextValue)
      return
    }

    await this.fetchDetails({ placeId, query, statusText: this.refreshAppliedTextValue })
  }

  async selectSuggestion(event) {
    event.preventDefault()

    const { placeId } = event.currentTarget.dataset
    if (!placeId) return

    this.placeIdInputTarget.value = placeId
    await this.fetchDetails({ placeId, statusText: this.selectionAppliedTextValue })
  }

  async fetchSuggestions(query) {
    this.abortPendingRequest()
    this.abortController = new AbortController()
    this.updateStatus(this.loadingTextValue)

    try {
      const response = await fetch(`${this.suggestionsPathValue}?query=${encodeURIComponent(query)}`, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        signal: this.abortController.signal
      })

      const payload = await response.json()
      if (!response.ok) throw new Error(payload.error || this.errorTextValue)

      this.renderSuggestions(payload.suggestions || [])
    } catch (error) {
      if (error.name === "AbortError") return

      console.error("Error loading Google suggestions:", error)
      this.hideSuggestions()
      this.updateStatus(error.message || this.errorTextValue)
    }
  }

  async fetchDetails({ placeId, query, statusText }) {
    this.abortPendingRequest()
    this.updateStatus(this.loadingTextValue)
    this.hideSuggestions()

    const params = new URLSearchParams()
    if (placeId) params.set("place_id", placeId)
    if (!placeId && query) params.set("query", query)

    try {
      const response = await fetch(`${this.detailsPathValue}?${params.toString()}`, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      const payload = await response.json()
      if (!response.ok) throw new Error(payload.error || this.errorTextValue)

      this.applyLocation(payload.location || {})
      this.updateStatus(statusText)
    } catch (error) {
      console.error("Error loading Google place details:", error)
      this.updateStatus(error.message || this.errorTextValue)
    }
  }

  applyLocation(location) {
    if (location.name && this.hasNameInputTarget) this.nameInputTarget.value = location.name
    if (location.address && this.hasAddressInputTarget) this.addressInputTarget.value = location.address
    if (location.latitude && this.hasLatitudeInputTarget) this.latitudeInputTarget.value = location.latitude
    if (location.longitude && this.hasLongitudeInputTarget) this.longitudeInputTarget.value = location.longitude
    if (location.maps_url && this.hasMapsUrlInputTarget) this.mapsUrlInputTarget.value = location.maps_url
    if (location.place_id && this.hasPlaceIdInputTarget) this.placeIdInputTarget.value = location.place_id
  }

  renderSuggestions(suggestions) {
    if (!suggestions.length) {
      this.suggestionsTarget.innerHTML = ""
      this.hideSuggestions()
      this.updateStatus(this.noResultsTextValue)
      return
    }

    this.suggestionsTarget.innerHTML = suggestions.map((suggestion) => {
      const mainText = suggestion.main_text || suggestion.description || ""
      const secondaryText = suggestion.secondary_text || ""

      return `
        <button
          type="button"
          class="flex w-full items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 text-left last:border-b-0 hover:bg-gray-50"
          data-action="location-lookup#selectSuggestion"
          data-place-id="${this.escapeHtml(suggestion.place_id || "")}" 
        >
          <span>
            <span class="block text-sm font-medium text-gray-900">${this.escapeHtml(mainText)}</span>
            <span class="block text-sm text-gray-500">${this.escapeHtml(secondaryText)}</span>
          </span>
          <span class="pt-0.5 text-xs font-medium uppercase tracking-wide text-primary-600">Google</span>
        </button>
      `
    }).join("")

    this.suggestionsTarget.classList.remove("hidden")
    this.updateStatus()
  }

  hideSuggestions() {
    if (this.hasSuggestionsTarget) {
      this.suggestionsTarget.classList.add("hidden")
    }
  }

  lookupQuery(preferredInput = null) {
    const preferredQuery = preferredInput?.value?.trim()
    if (preferredQuery) return preferredQuery

    return this.addressInputTarget.value.trim() || this.nameInputTarget.value.trim()
  }

  updateStatus(message = "") {
    if (this.hasStatusTarget) {
      const shouldShow = Boolean(message) && message !== this.idleTextValue

      this.statusTarget.textContent = shouldShow ? message : ""
      this.statusTarget.classList.toggle("hidden", !shouldShow)
    }
  }

  abortPendingRequest() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  clearDebounce() {
    if (this.debounceTimer) {
      window.clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }

  escapeHtml(value) {
    return value
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
